import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Typography, CardContent, Divider } from '@mui/material';
import NewProgress from '../../components/rating/NewProgress';
import { Rating } from 'react-simple-star-rating';
import { ReactComponent as StarsEmpty } from '../../assets/images/StarSVGEmpty.svg';
import { ReactComponent as StarsFilled } from '../../assets/images/StarSVGFilled.svg';
import { visualRating } from '../../util/EntertainmentUtil';
import { Circles } from 'react-loading-icons';
import BusinessProducts from '../Profile/BusinessProducts';

const BusinessSingle = () => {
  const { id } = useParams();
  const server_url = process.env.REACT_APP_API_URI;
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        const response = await axios.get(`${server_url}/business/${id}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        setCompanyData(response.data.business);
      } catch (err) {
        console.error('Error fetching business data:', err);
        setError('Error fetching business data.');
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessData();
  }, [id]);

  const calculateAverageRatings = () => {
    if (!companyData?.ratings?.length) return [];

    const questionRatingsMap = {};
    companyData.ratings.forEach((rating) => {
      rating.questions.forEach(({ questionId, question, rating }) => {
        if (!questionRatingsMap[questionId]) {
          questionRatingsMap[questionId] = {
            question,
            totalRating: 0,
            count: 0,
          };
        }
        questionRatingsMap[questionId].totalRating += rating;
        questionRatingsMap[questionId].count += 1;
      });
    });

    return Object.values(questionRatingsMap).map(
      ({ question, totalRating, count }) => {
        const rawAverage = totalRating / count;
        return { question, averageRating: rawAverage };
      },
    );
  };

  return (
    <div className="flex justify-center items-center min-h-[100vh-12rem] p-6">
      <div className="w-full max-w-5xl rounded-lg overflow-hidden">
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <Circles />
            </div>
          ) : error ? (
            <Typography className="text-center text-red-600 font-semibold">
              {error}
            </Typography>
          ) : companyData ? (
            <div className="space-y-6 bg-darkblue py-4 px-4 rounded-lg">
              <Typography
                variant="h4"
                className="font-bold text-white text-center"
              >
                {companyData.name}
              </Typography>
              <Divider className="bg-white h-0.5 rounded-lg" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 text-white">
                  <div>
                    <strong>Category:</strong> {companyData.category.join(', ')}
                  </div>
                  <div>
                    <strong>Market Location:</strong>{' '}
                    {companyData.marketLocation.join(', ')}
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <strong className="text-white">Market Size:</strong>{' '}
                    {companyData.marketSize}
                  </div>
                  <Typography variant="body1" color="text.secondary">
                    <strong className="text-white">Website:</strong>{' '}
                    <a
                      href={companyData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:underline"
                    >
                      {companyData.website}
                    </a>
                  </Typography>
                </div>
              </div>

              <div className="bg-primary/30 rounded-lg p-6 mb-6">
                <BusinessProducts
                  businessId={id}
                  businessCategory={companyData.category}
                  readOnly={true}
                />
              </div>

              <Divider className="bg-white h-1" />
              <h3 className="text-center text-2xl font-bold text-white">
                KnowQuest Rating
              </h3>
              <div className="flex items-center justify-center">
                <NewProgress kqRating={companyData.averageRating} />
              </div>
              {companyData.ratings?.length > 0 && (
                <div className="mt-4">
                  <div className="grid grid-cols-1 place-items-center gap-y-5 gap-x-4 text-white">
                    {calculateAverageRatings().map(
                      ({ question, averageRating }) => (
                        <div
                          className="grid w-full max-w-md md:max-w-lg lg:max-w-xl rounded-lg bg-primary p-4"
                          key={question}
                        >
                          <div className="flex flex-col lg:flex-row items-center justify-between p-2 gap-2">
                            <div
                              className={`aspect-square w-8 rounded grid place-items-center ${visualRating(
                                averageRating.toFixed(1),
                              )}`}
                            >
                              {averageRating.toFixed(1)}
                            </div>
                            <label className="text-center lg:text-left lg:pl-2">
                              {question}
                            </label>
                            <Rating
                              initialValue={averageRating}
                              allowFraction
                              readonly
                              emptyIcon={<StarsEmpty />}
                              fillIcon={<StarsFilled />}
                            />
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Typography className="text-center text-gray-600">
              No data found for this business.
            </Typography>
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default BusinessSingle;
